<template>
    <v-row justify="center" v-if="assistant.config">
        <v-dialog
            scrim="rgba(10,10,20,0.75)"
            v-model="assistant.visible"
            :fullscreen="false"
            persistent
            :max-width="assistant.config?.chatbox_width"
            transition="dialog-bottom-transition"
        >
            <div :class="['chat-ui-wrapper', assistant.theme === 'dark' ? 'dark' : 'light', 'animate-rise']" id="chat-ui">

                <!-- Header -->
                <div class="chat-header">
                    <div class="chat-header-left">
                        <div class="avatar-ring" v-if="assistant.config.icon_url">
                            <img class="chat-icon" :src="assistant.config.icon_url" alt="" />
                        </div>
                        <div class="header-text">
                            <span class="header-title">{{ assistant.config?.title }}</span>
                            <span class="header-status"><span class="status-dot"></span>Online</span>
                        </div>
                    </div>
                    <div class="header-actions">
                        <Buttons />
                    </div>
                </div>

                <!-- Chat Body -->
                <div class="chat-body">
                    <InitMessage v-if="!assistant.chats.length">
                        <div class="welcome-block">
                            <div class="welcome-text">{{ assistant.config.welcome_txt }}</div>
                            <div class="welcome-sub">{{ assistant.config.welcome_sub }}</div>
                            <ConvoStarters class="mt-4" v-if="!assistant.chats.length" @start="PredefinedConvo" />
                        </div>
                    </InitMessage>
                    <Message v-for="(chat, index) in assistant.chats" :key="chat.id ?? index" :chat="chat" />
                    <Message v-if="response.id" :chat="response" />
                </div>

                <!-- Input Area -->
                <div class="chat-footer">
                    <div class="prompt-box">
                        <textarea
                            ref="textareaRef"
                            class="prompt-input"
                            @keydown.enter.prevent="IdentifyIntent"
                            v-model="user_prompt"
                            placeholder="Ask anything..."
                            rows="1"
                        />
                        <button
                            class="send-btn"
                            @click="IdentifyIntent"
                            :disabled="!user_prompt || is_loading"
                        >
                            <v-icon v-if="!is_loading" icon="mdi-send" size="18" />
                            <v-progress-circular v-else indeterminate size="16" width="2" color="white" />
                        </button>
                    </div>
                    <div class="powered-by">Powered by AI · Press Enter to send</div>
                </div>

            </div>
        </v-dialog>
    </v-row>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import axios from "axios"
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import Message from "@/components/assistant/Message.vue"
import Buttons from "@/components/assistant/Buttons.vue"
import InitMessage from "@/components/assistant/InitMessage.vue"
import ConvoStarters from "@/components/assistant/ConvoStarters.vue"

const store = useGlobalsStore()
const { assistant, products, ConfigChatEnabled } = storeToRefs(store)

const is_loading = ref(false)
const user_prompt = ref('')
const user_intent = ref('no intent')
const textareaRef = ref<HTMLTextAreaElement | null>(null)

interface ChatMessage {
  id: number | null
  role: 'ai' | 'human' | 'system'
  message: string
  timestamp: string | null
}

const response = ref<ChatMessage>({
  id: null,
  role: "ai",
  message: "",
  timestamp: null,
})

const CatalogProductsToString = computed(() => {
  let products_string = ""
  products.value.forEach((product: any) => {
    products_string += `-#${product.id} ${product.name}\n`
  })
  return products_string
})

const History = computed(() => {
  if (!assistant.value.config) return []
  const { instructions, services } = assistant.value.config
  const history: Array<{ role: string; content: string }> = []

  // Instructions
  history.push({ role: "system", content: instructions })

  // Products, Supplies, Services list
  if (user_intent.value.includes('services')) {
    history.push({ role: "user", content: `Services List:\n${services}` })
  } else if (user_intent.value.includes('printers')) {
    history.push({ role: "user", content: `Printers List:\n${CatalogProductsToString.value}` })
  } else if (user_intent.value.includes('supplies')) {
    history.push({
      role: "user",
      content: `
        Supplies List:\n
        - Brother | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=brother
        - Canon | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=canon
        - Copystar | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=copystar
        - HP | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=hp
        - Hyperion | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=hyperion
        - Epson | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=epson
        - Konica Minolta | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=konica-minolta
        - Lexmark | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=lexmark
        - Ricoh | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=Ricoh
        - Samsung | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=samsung
        - Sharp | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=sharp
        - Xerox | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=xerox
        - Toshiba | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=toshiba
        - Others | https://shop.1800officesolutions.com/office-supplies/?pwb-brand-filter=others
      `
    })
  }

  // Chat history with the customer
  assistant.value.chats.forEach((chat: any) => {
    const role = chat.role === 'ai' ? 'assistant' : 'user'
    history.push({ role: role, content: chat.message })
  })

  history.push({ role: "user", content: user_prompt.value })
  return history
})

function AdjustHeight() {
  nextTick(() => {
    const textarea = textareaRef.value
    if (textarea) {
      textarea.style.height = "auto"
      textarea.style.height = textarea.scrollHeight + "px"
    }
  })
}

function ScrollToBottom() {
  const chatBody = document.querySelector('.chat-body')
  if (chatBody) {
    const observer = new MutationObserver(() => {
      chatBody.scrollTop = chatBody.scrollHeight
    })
    observer.observe(chatBody, { childList: true, subtree: true })
  }
}

function SendPrompt() {
  // Insert the user prompt
  store.AddChat({
    id: Date.now(),
    role: "human",
    message: user_prompt.value,
    timestamp: new Date().toISOString()
  })
  user_prompt.value = ""
  is_loading.value = true

  // Set initial AI response message
  response.value.id = Date.now()
  response.value.timestamp = new Date().toISOString()
  response.value.message = "Thinking..."

  const form = new FormData()
  form.append('history', JSON.stringify(History.value))

  axios.post("/wp-json/1-800-products/v1/assistant/send-prompt", form).then(res => {
    console.log("SendPrompt: ", res.data)
    const words = res.data.choices[0].message.content.split(" ")
    let count = 0
    response.value.message = ""
    const interval = setInterval(() => {
      if (count < words.length) {
        response.value.message += words[count] + " "
        count++
      } else {
        clearInterval(interval)
        store.AddChat({ ...response.value })
        response.value = {
          id: null,
          role: "ai",
          message: "",
          timestamp: null,
        }
      }
    }, 50)
  }).catch(error => {
    console.log("Here 2: ", error)
    store.SetError("Oops! Something went wrong. Please try again in a moment.")
  }).finally(() => {
    is_loading.value = false
  })
}

function IdentifyIntent() {
  if (!user_prompt.value) return

  // This decides whether to include the products, supplies, services to context
  const system_prompt = `
    Your task is to analyze the user input and chat history and determine if it expresses an intent related to any of the following categories:
        1. Services – Inquiries about the services we provide.
        2. Printers – Intent to lease or purchase a printer or VoIP phone.
        3. Supplies – Intent to purchase office supplies such as toners, parts, or consumables.

    Respond with one of the following plain text labels based on the identified intent:
        "services" – if the input relates to category 1
        "printers" – if the input relates to category 2
        "supplies" – if the input relates to category 3
        "no intent" – if none of the above categories clearly apply
    Only return the plain text label.
  `

  // Chat history with the customer
  const history = [{ role: "system", content: system_prompt }]
  assistant.value.chats.forEach((chat: any) => {
    const role = chat.role === 'ai' ? 'assistant' : 'user'
    history.push({ role: role, content: chat.message })
  })
  history.push({ role: "user", content: `user input: ${user_prompt.value}` })

  const form = new FormData()
  form.append('history', JSON.stringify(history))

  is_loading.value = true
  axios.post("/wp-json/1-800-products/v1/assistant/send-prompt", form).then(res => {
    console.log("IdentifyIntent: ", res.data)
    user_intent.value = res.data.choices[0].message.content
    SendPrompt()
  }).catch(error => {
    console.log("Here: ", error)
    store.SetError("Oops! Something went wrong. Please try again in a moment.")
  }).finally(() => {
    is_loading.value = false
  })
}

function PredefinedConvo(text: string) {
  user_prompt.value = text
  IdentifyIntent()
}

function HandleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape' && ConfigChatEnabled.value) {
    store.ToggleChatUI()
  }
}

onMounted(() => {
  window.addEventListener('keydown', HandleEscape)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', HandleEscape)
})

watch(user_prompt, () => {
  AdjustHeight()
})

watch(is_loading, (newVal) => {
  if (newVal) {
    ScrollToBottom()
  }
})
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');

@keyframes rise {
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
}

.animate-rise {
  animation: rise 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
}

/* ── Wrapper ── */
.chat-ui-wrapper {
  font-family: 'Inter', sans-serif;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  box-shadow:
    0 30px 80px rgba(0,0,0,0.25),
    0 0 0 1px rgba(255,255,255,0.08);
  backdrop-filter: blur(20px);

  /* Light theme */
  &.light {
    background: #ffffff;
    color: #1a1a2e;

    .chat-header    { background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); }
    .chat-body      { background: #f4f6fb; }
    .chat-footer    { background: #ffffff; border-top: 1px solid #e8eaf0; }
    .prompt-box     { background: #f0f2f8; }
    .prompt-input   { background: transparent; color: #1a1a2e; }
    .prompt-input::placeholder { color: #9ca3af; }
    .powered-by     { color: #9ca3af; }
  }

  /* Dark theme */
  &.dark {
    background: #0f0f1a;
    color: #e8eaf0;

    .chat-header    { background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%); border-bottom: 1px solid rgba(255,255,255,0.06); }
    .chat-body      { background: #0a0a14; }
    .chat-footer    { background: #0f0f1a; border-top: 1px solid rgba(255,255,255,0.06); }
    .prompt-box     { background: #1a1a2e; }
    .prompt-input   { background: transparent; color: #e8eaf0; }
    .prompt-input::placeholder { color: #4b5563; }
    .powered-by     { color: #4b5563; }

    .chat-bubble.from-ai   { background: #1e1e30; color: #e8eaf0; box-shadow: 0 4px 16px rgba(0,0,0,0.3); }
    .chat-bubble.from-human { box-shadow: 0 4px 16px rgba(59,130,246,0.25); }

    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #2a2a40; border-radius: 10px; }
  }
}

/* ── Header ── */
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  color: #ffffff;

  .chat-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .avatar-ring {
    padding: 2px;
    border-radius: 50%;
    background: rgba(255,255,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .chat-icon {
    height: 34px;
    width: 34px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  }

  .header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .header-title {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .header-status {
    font-size: 11px;
    opacity: 0.75;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4ade80;
    animation: pulse-dot 2s ease-in-out infinite;
    display: inline-block;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

/* ── Body ── */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  min-height: 300px;
  max-height: 420px;
  position: relative;
  scroll-behavior: smooth;

  &::-webkit-scrollbar       { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(100,100,150,0.2); border-radius: 10px; }

  .welcome-block {
    text-align: center;
    padding: 10px 20px;

    .welcome-text {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 6px;
      opacity: 0.9;
    }

    .welcome-sub {
      font-size: 13px;
      opacity: 0.55;
      line-height: 1.5;
      max-width: 260px;
      margin: 0 auto;
    }
  }
}

/* ── Footer ── */
.chat-footer {
  padding: 14px 16px 12px;
}

.prompt-box {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  border-radius: 14px;
  padding: 10px 10px 10px 14px;
  transition: box-shadow 0.2s;

  &:focus-within {
    box-shadow: 0 0 0 2px #3b82f6;
  }
}

.prompt-input {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  max-height: 160px;
  overflow-y: auto;
  padding: 2px 0;
}

.send-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s, opacity 0.15s, background 0.2s;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: #ffffff;

  &:hover:not(:disabled) { transform: scale(1.08); }
  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.powered-by {
  text-align: center;
  font-size: 10px;
  margin-top: 8px;
  letter-spacing: 0.03em;
}
</style>
